import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import SEOHead from '@/components/feature/SEOHead';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';

const featuredArticles = articles.filter((a) =>
  ['baisi-origin-history', 'baisi-jk-uniform', 'baisi-how-to-choose'].includes(a.slug)
);

const latestArticles = articles.slice(0, 6);

const categoryArticles: Record<string, typeof articles> = {};
categories.forEach((cat) => {
  categoryArticles[cat.slug] = articles.filter((a) => a.category === cat.slug).slice(0, 2);
});

export default function Home() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '白丝文化指南 - 首页',
    description:
      '探索白丝的多元文化内涵与时尚潮流，从白丝的起源、风格搭配到购买选购指南，为白丝爱好者提供最全面的参考资讯。',
    url: 'https://baisi-style.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '白丝文化指南',
      url: 'https://baisi-style.com',
    },
  };

  return (
    <>
      <SEOHead
        title="白丝｜白丝文化与潮流穿搭完全指南 - 探索白丝美学与个性表达"
        description="探索白丝的多元文化内涵与时尚潮流，从白丝的起源、风格搭配到购买选购指南，为白丝爱好者提供最全面的参考资讯，发现属于你的白丝美学与个性表达方式。"
        keywords="白丝,白丝搭配,白丝风格,白丝选购指南,白丝美学,白丝文化,白丝潮流穿搭"
        canonicalUrl="https://baisi-style.com/"
        ogType="website"
        schema={schema}
      />
      <Navbar />

      <main>
        <section className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Elegant%20white%20stockings%20fashion%20photography%20with%20soft%20dreamy%20lighting%2C%20minimalist%20aesthetic%2C%20cream%20and%20white%20tones%2C%20artistic%20composition%2C%20floating%20fabric%20texture%2C%20ethereal%20atmosphere%2C%20high%20fashion%20editorial&width=1800&height=900&seq=hero-baisi-home&orientation=landscape"
            alt="白丝文化与潮流穿搭"
            title="白丝文化指南 - 探索白丝美学"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/35 via-black/20 to-black/40"></div>
          <div className="relative z-10 w-full max-w-3xl mx-auto text-center px-4">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              探索白丝美学<br />发现个性表达
            </h1>
            <p className="text-base md:text-lg text-white/80 mb-8 max-w-2xl mx-auto leading-relaxed">
              从白丝的起源文化到潮流穿搭，从选购指南到视觉美学——你值得拥有的全方位白丝参考资讯平台
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/trend-outfit"
                className="px-8 py-3 bg-primary-500 text-white rounded-full font-medium text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                探索穿搭灵感
              </Link>
              <Link
                to="/category/buying-guide"
                className="px-8 py-3 bg-white/10 backdrop-blur-sm text-white border border-white/30 rounded-full font-medium text-sm hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
              >
                查看选购指南
              </Link>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10">
            <div className="w-6 h-10 border-2 border-white/40 rounded-full flex items-start justify-center">
              <div className="w-1.5 h-3 bg-white/60 rounded-full mt-2 animate-bounce"></div>
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                内容分类
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                四大内容板块，全方位覆盖白丝文化的每一个维度
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="group block bg-background-50 border border-background-200/70 rounded-lg p-6 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4 group-hover:bg-primary-200 transition-colors">
                    <i className={`${cat.icon} text-xl text-primary-600`}></i>
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-foreground-900 mb-2 whitespace-nowrap">
                    {cat.name}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {cat.description}
                  </p>
                  <div className="mt-4 flex items-center text-sm text-primary-600 font-medium whitespace-nowrap">
                    浏览文章
                    <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
                精选推荐
              </h2>
              <p className="text-foreground-600 text-sm md:text-base max-w-2xl mx-auto">
                编辑精心挑选的高质量内容，带你深入了解白丝的世界
              </p>
            </div>
            <div className="space-y-8">
              {featuredArticles.map((article, index) => (
                <ArticleCard key={article.slug} article={article} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {categories.map((cat) => {
          const catArts = categoryArticles[cat.slug] || [];
          if (catArts.length === 0) return null;
          return (
            <section key={cat.slug} className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-50">
              <div className="max-w-7xl mx-auto">
                <div className="flex items-center justify-between mb-10">
                  <div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                      {cat.name}
                    </h2>
                    <p className="text-sm text-foreground-600">{cat.description}</p>
                  </div>
                  <Link
                    to={`/category/${cat.slug}`}
                    className="hidden sm:flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看全部
                    <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
                  {catArts.map((article) => (
                    <ArticleCard key={article.slug} article={article} />
                  ))}
                </div>
                <div className="mt-6 text-center sm:hidden">
                  <Link
                    to={`/category/${cat.slug}`}
                    className="inline-flex items-center text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap cursor-pointer"
                  >
                    查看全部
                    <i className="ri-arrow-right-line ml-1"></i>
                  </Link>
                </div>
              </div>
            </section>
          );
        })}

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-2">
                  最新文章
                </h2>
                <p className="text-sm text-foreground-600">持续更新，发现更多精彩内容</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {latestArticles.map((article) => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full px-4 md:px-6 py-14 md:py-20 bg-accent-50">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
              加入白丝文化社群
            </h2>
            <p className="text-foreground-600 text-sm md:text-base mb-8 leading-relaxed">
              与万千白丝爱好者一起探索、交流、分享。关注我们获取最新穿搭灵感与文化资讯。
            </p>
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-3 bg-foreground-900 text-background-50 rounded-full font-medium text-sm hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              了解更多
              <i className="ri-arrow-right-line ml-2"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}